import { usePublishingCompletion } from 'components/CommonComponents/PublishingLoader/usePublishingCompletion'
import { useCallback, useRef, useState } from 'react'
import { useToast } from '@chakra-ui/core'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'config/redux'
import { FEATURE_FLAGS } from 'constants/featureFlags'
import useFeatureFlag from 'hooks/useFeatureFlag'
import { createTask, editTask, getInsightsV2 } from 'redux/actions/tasks_v2'
import { createPrompt, PROMPT_TYPES } from 'redux/actions/promts'
import { setResponseStatus } from 'redux/actions/general'
import { getUTCOffset } from 'utils'
import { TaskFormData } from '../model/types'
import { buildTaskSubmission, cleanSubtaskPayload } from '../model/taskSubmission'

interface TaskPublicationOptions {
  getFormData: () => Partial<TaskFormData>
  isEdit: boolean
  tid?: string
  isGroupedByCategory: boolean
  onClose?: VoidFunction
}

/** Owns publication sequencing; Redux thunks remain the authenticated transport/cache boundary. */
export function useTaskPublication({ getFormData, isEdit, tid, isGroupedByCategory, onClose }: TaskPublicationOptions) {
  const dispatch: AppDispatch = useDispatch()
  const toast = useToast()
  const isAiVmEnabled = useFeatureFlag(FEATURE_FLAGS.AI_VM)

  const [isSubmitting, setIsSubmitting] = useState(false)

  const submittingRef = useRef(false)
  const { publicationComplete, resetCompletion, showCompletion } = usePublishingCompletion()

  const submitAllForms = useCallback(async () => {
    // State alone cannot guard two clicks in the same render or imperative submit callbacks.
    if (submittingRef.current) return
    submittingRef.current = true
    resetCompletion()
    setIsSubmitting(true)

    try {
      const { createTaskPayload, editTaskPayload } = buildTaskSubmission(getFormData(), {
        utcOffset: getUTCOffset(),
        aiEnabled: isAiVmEnabled,
        groupedByCategory: isGroupedByCategory,
      })

      if (isEdit && tid) {
        const isOk = await dispatch(editTask({ ...editTaskPayload, tid }))
        if (isOk) {
          if (await showCompletion()) {
            dispatch(setResponseStatus(true))
            onClose?.()
          }
        }
        return
      }

      const { groups, users, tags, data } = createTaskPayload
      let subtasksWithAiPrompts = createTaskPayload.sub_tasks
      try {
        subtasksWithAiPrompts = await Promise.all(
          createTaskPayload.sub_tasks.map(async (subtask) => {
            const prompt = (subtask.ai_prompt || '').trim()

            if (!subtask.is_ai_enhanced || !prompt) return subtask

            const promptId = await dispatch(
              createPrompt({
                prompt,
                users,
                groups,
                tags,
                data: { prompt_type: PROMPT_TYPES.CUSTOM, title: '', content: subtask.title || data.title },
              })
            )

            return { ...subtask, message_id: promptId }
          })
        )
      } catch (error) {
        console.error('AI prompt registration failed:', error)
        toast({
          title: 'AI prompt registration failed',
          description: 'The task was not created. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      const createdId = await dispatch(
        createTask({ ...createTaskPayload, sub_tasks: subtasksWithAiPrompts.map(cleanSubtaskPayload) })
      )
      if (createdId) {
        dispatch(getInsightsV2({}))
        if (await showCompletion()) {
          dispatch(setResponseStatus(true))
          onClose?.()
        }
      } else {
        toast({
          title: 'Task creation failed',
          description: 'Something went wrong. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    } catch (error) {
      console.error('Task creation failed:', error)
      toast({
        title: 'Task creation failed',
        description: 'Something went wrong. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      submittingRef.current = false
      setIsSubmitting(false)
    }
  }, [
    getFormData,
    isEdit,
    tid,
    dispatch,
    onClose,
    isGroupedByCategory,
    isAiVmEnabled,
    toast,
    resetCompletion,
    showCompletion,
  ])

  return { submitAllForms, isSubmitting, publicationComplete }
}
